import { css } from '@emotion/react';
import { ScheduleRequest } from '@weave/schema-gen-ts/dist/schemas/schedule/api/v2/api.pb';
import { AppointmentType } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/appointment_type.pb';
import { useDevToolsStore } from '@frontend/devtools';
import { ScheduleRequestListItem } from '@frontend/schedule-helpers';
import { theme } from '@frontend/theme';
import { ContentLoader, Text, Truncated, useModalControl } from '@frontend/design-system';
import { ColorDot } from '../../../../../../../components/appointment-panel/color-dot';
import { PRIMARY_EVENT_COLOR } from '../../../../../../../constants';
import { useScheduleRequestActions } from '../../hooks/use-schedule-request-actions';
import { ScheduleRequestDeleteConfirmationModal } from '../components/schedule-request-delete-confirmation-modal';
import { ScheduleRequestDismissConfirmationModal } from '../components/schedule-request-dismiss-confirmation-modal';
import { ScheduleRequestRejectConfirmationModal } from '../components/schedule-request-reject-confirmation-modal';
import { ScheduleRequestItemHeader } from '../schedule-request-item-header';

export type SchedulePulseRequestListItemProps = {
  listItem: ScheduleRequestListItem;
  setSelectedScheduleRequest: (request: ScheduleRequest | null) => void;
  scheduleRequestModalProps: ReturnType<typeof useModalControl>;
  appointmentTypes: AppointmentType[];
};

export const ScheduleRequestOverlappingListItem = (props: SchedulePulseRequestListItemProps) => {
  const { listItem, appointmentTypes } = props;
  const isPatientContacted = listItem.status === 'MESSAGED' || listItem.status === 'CALLED';
  const patientContactedAt = isPatientContacted ? listItem.reviewedAt : undefined;
  const appointmentType = appointmentTypes.find((type) => type.id === listItem.appointmentTypeId);

  const isDebugModeOn = useDevToolsStore((state) => state.options.isDebugModeOn);

  const deleteRequestModalProps = useModalControl();
  const dismissRequestModalProps = useModalControl();
  const rejectRequestModalProps = useModalControl();

  const {
    isLoading,
    handleMessage,
    handlePhoneCall,
    handleCompleteRequest,
    handleDeleteRequest,
    handleDismissRequest,
    setDismissActionCalledFrom,
    handleRejectRequest,
  } = useScheduleRequestActions(listItem);

  return (
    <>
      <div
        css={[
          accordionHeaderOverrideStyles,
          debugModeV2Styles(isDebugModeOn && listItem.linkHash === 'v2'),
          itemContainerStyle,
        ]}
      >
        <ContentLoader show={isLoading} size='small' />
        <ScheduleRequestItemHeader
          {...props}
          patientContactedAt={patientContactedAt}
          deleteRequestModalProps={deleteRequestModalProps}
          dismissRequestModalProps={dismissRequestModalProps}
          rejectRequestModalProps={rejectRequestModalProps}
          onCompleteRequest={handleCompleteRequest}
          onDismissRequestIntent={setDismissActionCalledFrom}
          onPhoneCall={handlePhoneCall}
          onMessage={handleMessage}
          variant='overlappingPanel'
          moreInfo={
            <div
              css={{
                display: 'flex',
                gap: theme.spacing(0.5),
                alignItems: 'center',
                minWidth: 0,
                marginTop: theme.spacing(0.5),
              }}
            >
              <ColorDot size='small' color={appointmentType?.details?.clr || PRIMARY_EVENT_COLOR} />
              <Truncated as={Text} size='small' color='subdued'>
                {appointmentType?.displayName || '-'}
              </Truncated>
            </div>
          }
        />
      </div>
      <ScheduleRequestDeleteConfirmationModal onConfirm={handleDeleteRequest} {...deleteRequestModalProps.modalProps} />
      <ScheduleRequestRejectConfirmationModal onConfirm={handleRejectRequest} {...rejectRequestModalProps.modalProps} />
      <ScheduleRequestDismissConfirmationModal
        onConfirm={handleDismissRequest}
        {...dismissRequestModalProps.modalProps}
      />
    </>
  );
};

const accordionHeaderOverrideStyles = css`
  .header-container {
    padding: var(--fabric-space-padding-sm) var(--fabric-space-padding-md);
  }
`;

const itemContainerStyle = css({
  border: `1px solid ${theme.colors.neutral20}`,
  borderRadius: theme.borderRadius.medium,
  backgroundColor: theme.colors.white,
  transition: 'box-shadow 0.2s ease-in-out',
  padding: theme.spacing(1, 2),
  margin: theme.spacing(2),
  ':hover': {
    boxShadow: theme.shadows.light,
    cursor: 'pointer',
  },
});

const debugModeV2Styles = (isDebugModeOn: boolean) =>
  css`
    ${isDebugModeOn ? `background-color: var(--fabric-color-red-10);` : ''}
  `;
